import { capitalize } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  AdvancedDataGrid,
  Avatar,
  createDataCardInitialColumnDefinition,
  createInfoCardColumnDefinition,
  MenuAction,
  NestedDataCardProps,
  SelectedFilters,
  SingleStatusChip,
} from "@inductiveautomation/ws-components";

import { licenseActions } from "./Licenses.actions";
import { MAX_MAKER_LICENSES_COUNT } from "./Licenses.data";
import { MakerLicenseSummaryItem } from "./api/makerLicenses.api";
import { addDashToKey } from "./License/utilities";

interface MakerLicenseSummaryItemWithFkey extends MakerLicenseSummaryItem {
  fkey: string;
}

const LicensesDataGrid = ({
  licenses,
  onAction,
}: {
  licenses: MakerLicenseSummaryItem[];
  onAction: (actionId: string, licenseIds: string[]) => void;
}) => {
  const formattedLicenses = licenses.map((license) => {
    return {
      ...license,
      // purely for display purposes, test ids will remain as key
      fkey: addDashToKey(license.key),
    };
  });

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>();

  const actionMapper = licenseActions(t, onAction);
  const mapper = licenseToDataCardMapper(actionMapper);
  const availableStatuses = ["available", "active"];

  const statusFilter = selectedFilters?.["filter-status"];
  const filteredData = formattedLicenses.filter((license) => {
    let statusMatches = true;

    if (statusFilter instanceof Array && typeof statusFilter[0] === "string") {
      statusMatches = statusFilter.includes(
        `status-${license.status.toLowerCase()}`
      );
    }

    return statusMatches;
  });
  const filteredDataCards = filteredData.map(mapper);
  return (
    <AdvancedDataGrid
      testId="maker-licenses-data-grid"
      chromeless
      headings={[
        {
          id: "maker-licenses-heading-license",
          label: t("maker_licenses.data_grid.headings.license"),
        },
        {
          id: "maker-licenses-heading-status",
          label: t("maker_licenses.data_grid.headings.status"),
          alignment: "right",
        },
      ]}
      labels={{
        singular_item_name: t("maker_licenses.data_grid.singular_item_name"),
        plural_item_name: t("maker_licenses.data_grid.plural_item_name"),
      }}
      data={filteredDataCards}
      filterChips={[
        {
          id: "filter-status",
          label: t("maker_licenses.data_grid.filters.status"),
          variant: "list_advanced",
          listItems: [
            availableStatuses.map((status) => ({
              id: `status-${status}`,
              label: capitalize(status),
              variant: "checkbox" as const,
            })),
          ],
        },
      ]}
      onSelection={(rowId) => {
        if (rowId) {
          void navigate(`/maker-licenses/${rowId}`);
        }
      }}
      selectedFilters={selectedFilters}
      onFiltersUpdated={setSelectedFilters}
      generateCsvData={() => ({
        headings: [
          t("maker_licenses.data_grid.headings.key"),
          t("maker_licenses.data_grid.headings.name"),
          t("maker_licenses.data_grid.headings.status"),
        ],
        rows: filteredData.map((row) => [row.fkey, row.name ?? "", row.status]),
      })}
      footerProps={{
        helperText: t("maker_licenses.licenses_available", {
          count: MAX_MAKER_LICENSES_COUNT - licenses.length,
        }),
      }}
      customDummyCard={{
        id: "empty-maker-license-set",
        disabled: true,
        hasDivider: false,
        contentColumns: [
          {
            kind: "primary-with-avatar",
            content: {
              heading: t("maker_licenses.data_grid.empty_heading"),
              avatar: <Avatar icon="license" iconAvatarColor="neutral" />,
              parentTestId: "maker-licenses-data-grid",
            },
          },
        ],
      }}
    />
  );
};

export default LicensesDataGrid;

const licenseToDataCardMapper =
  (actionMapper: (license: MakerLicenseSummaryItem) => MenuAction[][]) =>
  (license: MakerLicenseSummaryItemWithFkey): NestedDataCardProps => ({
    id: license.key,
    testId: `maker-license-${license.key}`,
    variant: "checkbox",
    contentColumns: [
      createDataCardInitialColumnDefinition({
        parentTestId: license.key,
        avatar: <Avatar icon="license" iconAvatarColor="maker" />,
        heading: license.fkey,
        subtext: license.name,
      }),
      {
        ...createInfoCardColumnDefinition(
          <SingleStatusChip
            testId="maker-license-status-chip"
            label={capitalize(license.status.toLowerCase())}
          />
        ),
        sortValue: () => license.status,
      },
    ],
    actionLists: actionMapper(license),
    childItems: [],
  });
